import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Clock, CheckCircle, XCircle, 
  MessageCircle, TrendingUp, TrendingDown, Search, Filter
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';

export default function OrderHistory() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [chatHistory, setChatHistory] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'buy', 'sell'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Load chat history from localStorage
    const history = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    setChatHistory(history);
  }, []);

  const filteredHistory = chatHistory.filter(chat => {
    // Type filter
    if (filter !== 'all' && chat.orderData?.type !== filter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        chat.orderData?.id?.toLowerCase().includes(query) ||
        chat.orderData?.server?.toLowerCase().includes(query) ||
        chat.orderData?.character?.toLowerCase().includes(query) ||
        (chat.orderData?.quantity?.toString() || '').includes(query);
      
      return matchesSearch;
    }
    
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-yellow-400';
      case 'completed': return 'text-green-400';
      case 'cancelled': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Clock size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewChat = (chatId) => {
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) {
      // Store the selected chat and navigate to customer service
      localStorage.setItem('selectedChat', JSON.stringify(chat));
      navigate('/customer-service', { state: { orderData: chat.orderData, chatHistory: chat.messages } });
    }
  };

  const handleCancelOrder = (e, chat) => {
    e.stopPropagation(); // Prevent navigating to chat
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) return;

    // Add system message about cancellation
    const cancelMessage = {
      id: Date.now(),
      sender: 'system',
      text: '❌ Commande annulée par l\'utilisateur',
      timestamp: new Date().toISOString(),
      status: 'read'
    };

    // Update chat history
    const updatedHistory = chatHistory.map(c => {
      if (c.id === chat.id) {
        return {
          ...c,
          messages: [...(c.messages || []), cancelMessage],
          orderData: { ...c.orderData, status: 'cancelled' },
          status: 'cancelled'
        };
      }
      return c;
    });
    
    setChatHistory(updatedHistory);
    localStorage.setItem('chatHistory', JSON.stringify(updatedHistory));

    // Update admin orders
    const adminOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
    const updatedAdminOrders = adminOrders.map(order => 
      order.id === chat.orderData?.id ? { ...order, status: 'cancelled' } : order
    );
    localStorage.setItem('admin_orders', JSON.stringify(updatedAdminOrders));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold">Connexion requise</h2>
          <p className="text-gray-400 mb-4">Veuillez vous connecter pour voir votre historique</p>
          <Link
            to="/login"
            className="bg-yellow-500 text-black px-6 py-3 rounded-lg font-medium hover:bg-yellow-400"
          >
            Se connecter
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/" className="p-1.5 sm:p-2 -ml-1.5 hover:bg-gray-800 rounded-full flex-shrink-0" aria-label="Retour">
                <ArrowLeft size={20} />
              </Link>
              <h1 className="text-base sm:text-xl font-bold leading-tight">Historique des Commandes</h1>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-6 flex-shrink-0">
              <div className="text-xs sm:text-sm text-gray-400 whitespace-nowrap">
                <span className="font-medium">{chatHistory.length}</span> commande{chatHistory.length > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex gap-2 sm:gap-4">
            {/* Type Filter */}
            <div className="flex gap-2 items-center flex-shrink-0">
              <label className="text-xs sm:text-sm text-gray-400">Type:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="all">Tous</option>
                <option value="buy">Achats</option>
                <option value="sell">Ventes</option>
              </select>
            </div>

            {/* Search */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par ID, serveur, personnage..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {filteredHistory.length === 0 ? (
          <div className="text-center py-10 sm:py-16">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-400 mb-2">Aucune commande trouvée</h3>
            <p className="text-gray-500">
              {filter === 'all' && !searchQuery 
                ? 'Vous n\'avez aucune commande pour le moment.' 
                : 'Aucune commande ne correspond à votre recherche.'}
            </p>
            <Link
              to="/buy"
              className="inline-flex items-center gap-2 bg-yellow-500 text-black px-6 py-3 rounded-lg font-medium hover:bg-yellow-400 mt-4"
            >
              <TrendingUp size={18} />
              Nouvel achat
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
            {filteredHistory.map((chat) => (
              <motion.div
                key={chat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-900 border border-gray-800 rounded-xl p-3 sm:p-5 hover:border-yellow-500/50 transition-colors cursor-pointer"
                onClick={() => handleViewChat(chat.id)}
              >
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
                      chat.orderData?.type === 'buy' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}>
                      {chat.orderData?.type === 'buy' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm sm:text-base truncate">
                        {chat.orderData?.type === 'buy' ? 'Achat' : 'Vente'} #{chat.orderData?.id?.slice(-6) || 'N/A'}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-400 truncate">
                        {chat.orderData?.server} • {chat.orderData?.quantity}M Kamas
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    <div className={`flex items-center gap-1 text-xs sm:text-sm ${getStatusColor(chat.status)}`}>
                      {getStatusIcon(chat.status)}
                      {chat.status === 'active' ? 'En cours' : chat.status}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(chat.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-800 pt-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-2 sm:gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 text-xs">Personnage:</span>
                      <p className="text-white font-medium text-xs sm:text-sm truncate">{chat.orderData?.character || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Prix total:</span>
                      <p className="text-white font-medium text-xs sm:text-sm">{chat.orderData?.totalPrice?.toFixed(2)} DH</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Email:</span>
                      <p className="text-white font-medium text-xs sm:text-sm truncate">{chat.orderData?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Messages:</span>
                      <p className="text-white font-medium text-xs sm:text-sm">{chat.messages?.length || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                    <MessageCircle size={16} />
                    <span>Cliquez pour voir le chat</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{chat.orderData?.category}</span>
                    {chat.orderData?.status !== 'cancelled' && chat.status !== 'cancelled' && (
                      <button
                        onClick={(e) => handleCancelOrder(e, chat)}
                        className="px-3 py-1.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-xs hover:bg-red-500/30 transition-colors flex items-center gap-1"
                      >
                        <XCircle size={14} />
                        Annuler
                      </button>
                    )}
                    {(chat.orderData?.status === 'cancelled' || chat.status === 'cancelled') && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                        Annulée
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
